/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { useServiceContext } from '~/root'
import Modal from '~/components/Modal'
import { Button } from '~/components/ui'
import Info from './Info/Info'
import Level from './level/Level'
import PackageContents from './package-contents/PackageContents'
import PackageTags from './package-tags/PackageTags'

interface ApplyPackageStep1Props {
  nextStep: (packageData: any, optionsData: any, configData: any, nextStepItem: string) => void
  doClose: () => void
  packageData: any
  config: any
  options: any
  updateConfigData: (data: any) => void
  updateOptionsData: (data: any) => void
  updatePackageData: (data: any) => void
}

const ApplyPackageStep1: React.FC<ApplyPackageStep1Props> = ({
  nextStep,
  doClose,
  packageData,
  config,
  options,
  updateConfigData,
  updateOptionsData,
  updatePackageData
}) => {
  const formRef = useRef<HTMLFormElement | null>(null)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [pckgData, setPckgData] = useState<any>(null)
  const [optionsData, setOptionsData] = useState<any>(null)
  const [configData, setConfigData] = useState<any>(null)

  useEffect(() => {
    setPckgData(packageData)
  }, [packageData])

  useEffect(() => {
    setOptionsData(options)
  }, [options])

  useEffect(() => {
    setConfigData(config)
  }, [config])

  const gotoStep2 = () => {
    nextStep(pckgData, optionsData, configData, 'REVIEW')
  }

  return (
    <>
      <Modal.Body>
        <div className="apply-package-form step-one">
          <Info packageData={packageData} />
          <Level
            packageData={pckgData}
            config={configData}
            options={optionsData}
            updateConfigData={(data: any) => {
              const configDataLocal = cloneDeep(configData)
              if (configDataLocal) {
                configDataLocal.levels = data
                setConfigData((oldData: any) => ({ ...oldData, levels: configDataLocal.levels }))
                updateConfigData(configDataLocal)
              }
            }}
            updateOptionsData={(data: any) => {
              const optionsDataLocal = options
              if (data && optionsDataLocal) {
                optionsDataLocal.levels = data
                setOptionsData((oldData: any) => ({ ...oldData, levels: data }))
                updateOptionsData(optionsDataLocal)
              }
            }}
          />
          <PackageContents
            packageData={pckgData}
            config={config}
            options={options}
            updatePackageData={(pckgDataRef: any) => {
              if (pckgDataRef) {
                setPckgData(pckgDataRef)
                updatePackageData(pckgDataRef)
              }
            }}
            updateConfigData={(data: any) => {
              const configDataLocal = cloneDeep(configData)
              if (configDataLocal) {
                configDataLocal.contents = data
                setConfigData((oldData: any) => ({ ...oldData, contents: configDataLocal.contents }))
                updateConfigData(configDataLocal)
              }
            }}
          />
          <PackageTags
            packageData={packageData}
            config={config}
            options={options}
            updateConfigData={(data: any) => {
              const configDataLocal = cloneDeep(configData)
              if (configDataLocal) {
                configDataLocal.tags = data
                setConfigData((oldData: any) => ({ ...oldData, tags: configDataLocal.tags }))
                updateConfigData(configDataLocal)
              }
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <Button
            id="7f8cd2a7-b861-4098-aeb8-a12570801fde"
            bsStyle="link"
            className="text-capitalize"
            onClick={doClose}
            type="button"
          >
            {translate('CANCEL')}
          </Button>
          <Button
            id="493f070e-f4eb-4453-8bb3-5b7bd5d9ecaf"
            onClick={gotoStep2}
            className="btn btn-primary text-capitalize"
            type="submit"
          >
            {translate('NEXT')}
          </Button>
        </div>
      </Modal.Footer>
    </>
  )
}

export default ApplyPackageStep1
